<template>
    <el-dialog :title="$t('common.sendEmail')" :visible.sync="sendDialogVisible" width="640px"
               custom-class="document-email-dialog" :close-on-click-modal="false"
               :close-on-press-escape="!sendLoading" :show-close="!sendLoading">
        <div v-if="!batchMode" class="document-email-form">
            <div class="document-email-field">
                <label>{{$t('invoiceCreate.labelTo')}}</label>
                <el-input v-model="sendForm.toEmail" :placeholder="$t('invoiceCreate.placeholderAddRecipient')"></el-input>
            </div>
            <div class="document-email-row">
                <div class="document-email-field">
                    <label>{{$t('invoiceCreate.cc')}}</label>
                    <el-input v-model="sendForm.ccEmail" :placeholder="$t('invoiceCreate.cc')"></el-input>
                </div>
                <div class="document-email-field">
                    <label>{{$t('invoiceCreate.bcc')}}</label>
                    <el-input v-model="sendForm.bccEmail" :placeholder="$t('invoiceCreate.bcc')"></el-input>
                </div>
            </div>
            <div class="document-email-field">
                <label>{{$t('invoiceCreate.labelSubject')}}</label>
                <el-input v-model="sendForm.subject" :placeholder="$t('invoiceCreate.placeholderEmailSubject')"></el-input>
            </div>
            <div class="document-email-field">
                <label>{{$t('default.emailMessage')}}</label>
                <el-input v-model="sendForm.content" type="textarea" :rows="6"
                          :placeholder="$t('invoiceCreate.placeholderMessage')"></el-input>
            </div>
            <div class="document-email-attachment">
                <div class="document-email-pdf-icon">PDF</div>
                <div>
                    <strong>{{attachmentName}}</strong>
                    <span>{{$t('invoiceCreate.pdf')}}</span>
                </div>
            </div>
        </div>
        <div v-else class="batch-email-form">
            <div class="batch-email-intro">{{selectedDocumentCount}} {{$t('common.selected')}}</div>
            <el-collapse v-model="expandedBatchDocuments" accordion>
                <el-collapse-item v-for="document in batchDocuments" :key="document.invoiceUUID"
                                  :name="document.invoiceUUID">
                    <template slot="title">
                        <div class="batch-email-title">
                            <strong>{{document.attachmentName}}</strong>
                            <span>{{document.toEmail || $t('invoiceCreate.placeholderAddRecipient')}}</span>
                            <i v-if="document.status === 'sent'" class="el-icon-success batch-email-success"></i>
                            <i v-else-if="document.status === 'failed'" class="el-icon-error batch-email-failed"></i>
                        </div>
                    </template>
                    <div class="document-email-form batch-email-document">
                        <div class="document-email-field">
                            <label>{{$t('invoiceCreate.labelTo')}}</label>
                            <el-input v-model="document.toEmail" :placeholder="$t('invoiceCreate.placeholderAddRecipient')"></el-input>
                        </div>
                        <div class="document-email-row">
                            <div class="document-email-field">
                                <label>{{$t('invoiceCreate.cc')}}</label>
                                <el-input v-model="document.ccEmail" :placeholder="$t('invoiceCreate.cc')"></el-input>
                            </div>
                            <div class="document-email-field">
                                <label>{{$t('invoiceCreate.bcc')}}</label>
                                <el-input v-model="document.bccEmail" :placeholder="$t('invoiceCreate.bcc')"></el-input>
                            </div>
                        </div>
                        <div class="document-email-field">
                            <label>{{$t('invoiceCreate.labelSubject')}}</label>
                            <el-input v-model="document.subject" :placeholder="$t('invoiceCreate.placeholderEmailSubject')"></el-input>
                        </div>
                        <div class="document-email-field">
                            <label>{{$t('default.emailMessage')}}</label>
                            <el-input v-model="document.content" type="textarea" :rows="4"
                                      :placeholder="$t('invoiceCreate.placeholderMessage')"></el-input>
                        </div>
                        <div class="document-email-attachment">
                            <div class="document-email-pdf-icon">PDF</div>
                            <div>
                                <strong>{{document.attachmentName}}</strong>
                                <span>{{$t('invoiceCreate.pdf')}}</span>
                            </div>
                        </div>
                        <div v-if="document.errorMessage" class="batch-email-error">{{document.errorMessage}}</div>
                    </div>
                </el-collapse-item>
            </el-collapse>
        </div>
        <div slot="footer" class="document-email-footer">
            <el-button :disabled="sendLoading" @click="sendDialogVisible = false">{{$t('common.cancel')}}</el-button>
            <el-button type="primary" :loading="sendLoading" @click="batchMode ? sendBatchEmail() : sendEmail()">
                <i class="el-icon-position"></i>
                <span>{{$t('common.send')}}</span>
            </el-button>
        </div>
    </el-dialog>
</template>

<script>
    import moment from 'moment-timezone'
    import {timest, validateEmail} from '@/utils/validate'
    import {containsHtml} from '@/utils/emailContent'
    import {reportHandledError} from '@/utils/sentry'

    export default {
        props: {
            createType: {
                type: Number,
                required: true
            },
            documentName: {
                type: String,
                required: true
            },
            companyId: {
                type: String,
                required: true
            },
            companyName: {
                type: String,
                required: true
            },
            setting: {
                type: Object,
                required: true
            }
        },
        data() {
            return {
                sendDialogVisible: false,
                sendLoading: false,
                invoiceUUID: "",
                pdfName: "",
                attachmentName: "",
                batchMode: false,
                batchDocuments: [],
                expandedBatchDocuments: "",
                sendForm: {
                    toEmail: "",
                    ccEmail: "",
                    bccEmail: "",
                    subject: "",
                    content: ""
                }
            }
        },
        methods: {
            openLoading() {
                const listPage = this.$el.closest('.list-page');
                return this.$loading({
                    target: listPage,
                    lock: false,
                    fullscreen: false,
                    text: this.$t('common.preparingPdf'),
                    spinner: 'el-icon-loading',
                    background: 'rgba(15, 23, 42, 0.18)',
                    customClass: 'document-action-loading-mask'
                });
            },
            buildPreviewUrl(invoiceUUID) {
                const loginInformation = JSON.parse(localStorage.getItem("loginInformation")).objectData;
                const previewBaseUrl = process.env.NODE_ENV === 'development'
                    ? window.location.origin + '/'
                    : this.$store.state.requestURL;
                const previewUrl = new URL('preview_pdf', previewBaseUrl);
                previewUrl.searchParams.set('invoiceUUID', invoiceUUID);
                previewUrl.searchParams.set('email', loginInformation.email);
                previewUrl.searchParams.set('userUuid', loginInformation.userUuid);
                previewUrl.searchParams.set('companyUUID', this.companyId);
                previewUrl.searchParams.set('nowTime', timest());
                previewUrl.searchParams.set('createType', String(this.createType));
                previewUrl.searchParams.set('aaa', String(Math.round(Math.random() * 10)));
                previewUrl.searchParams.set('timeont', moment.tz.guess());
                return previewUrl.toString();
            },
            async fetchPdf(invoiceUUID) {
                const response = await fetch(this.buildPreviewUrl(invoiceUUID), {
                    method: 'GET',
                    cache: 'no-store',
                    credentials: 'omit'
                });
                if (!response.ok) {
                    throw new Error(this.$t('common.pdfPreparationFailed') + ` (HTTP ${response.status})`);
                }
                const pdf = await response.blob();
                const signature = await pdf.slice(0, 5).text();
                if (signature !== '%PDF-') {
                    throw new Error(this.$t('common.pdfPreparationFailed'));
                }
                return pdf;
            },
            async printDocument(row) {
                const loading = this.openLoading();
                try {
                    const pdf = await this.fetchPdf(row.innerUUID);
                    loading.close();
                    this.printPdf(pdf);
                } catch (error) {
                    reportHandledError(error, {
                        module: 'document_actions',
                        operation: 'print_document',
                        context: {createType: this.createType}
                    });
                    loading.close();
                    this.$message.error(error.message);
                }
            },
            async printDocuments(rows) {
                if (!Array.isArray(rows) || rows.length === 0) {
                    return;
                }
                if (rows.length === 1) {
                    await this.printDocument(rows[0]);
                    return;
                }
                const loading = this.openLoading();
                try {
                    const loginInformation = JSON.parse(localStorage.getItem("loginInformation")).objectData;
                    const response = await this.$axios.post('web_batch_preview_pdf', {
                        params: {
                            invoiceUUIDList: rows.map(row => row.innerUUID),
                            createType: this.createType,
                            companyUUID: this.companyId,
                            email: loginInformation.email,
                            userUuid: loginInformation.userUuid,
                            timeont: moment.tz.guess(),
                            nowTime: timest()
                        }
                    }, {
                        responseType: 'arraybuffer',
                        timeout: 120000
                    });
                    const pdf = new Blob([response.data], {type: 'application/pdf'});
                    const signature = await pdf.slice(0, 5).text();
                    if (signature !== '%PDF-') {
                        throw new Error(this.$t('common.pdfPreparationFailed'));
                    }
                    loading.close();
                    this.printPdf(pdf);
                } catch (error) {
                    reportHandledError(error, {
                        module: 'document_actions',
                        operation: 'print_documents',
                        context: {
                            createType: this.createType,
                            documentCount: rows.length
                        }
                    });
                    loading.close();
                    this.$message.error(error.message);
                }
            },
            printPdf(pdf) {
                const pdfUrl = URL.createObjectURL(pdf);
                const printFrame = document.createElement('iframe');
                printFrame.setAttribute('aria-hidden', 'true');
                printFrame.style.position = 'fixed';
                printFrame.style.right = '0';
                printFrame.style.bottom = '0';
                printFrame.style.width = '1px';
                printFrame.style.height = '1px';
                printFrame.style.border = '0';
                printFrame.style.opacity = '0';

                const cleanUp = () => {
                    URL.revokeObjectURL(pdfUrl);
                    printFrame.remove();
                };
                printFrame.onload = () => {
                    try {
                        const printWindow = printFrame.contentWindow;
                        printWindow.addEventListener('afterprint', cleanUp, {once: true});
                        printWindow.focus();
                        printWindow.print();
                        window.setTimeout(cleanUp, 60000);
                    } catch (error) {
                        reportHandledError(error, {
                            module: 'document_actions',
                            operation: 'open_print_dialog',
                            context: {createType: this.createType}
                        });
                        cleanUp();
                        this.$message.error(error.message);
                    }
                };
                printFrame.src = pdfUrl;
                document.body.appendChild(printFrame);
            },
            async openSendDialog(row) {
                const loading = this.openLoading();
                try {
                    const detailResponse = await this.$axios.post('web_query_invoice', {
                        params: {
                            innerUUID: row.innerUUID,
                            companyUUID: this.companyId
                        }
                    });
                    if (detailResponse.data.errorCode !== 0) {
                        return;
                    }

                    const invoice = detailResponse.data.invoice;
                    const userClient = detailResponse.data.userClient;
                    const documentPrefix = this.createType === 0 ? 'Invoice' : this.documentName;

                    this.invoiceUUID = invoice.innerUUID;
                    this.pdfName = invoice.numPrefix + invoice.num + ".pdf";
                    this.attachmentName = documentPrefix + "-" + this.pdfName;
                    this.sendForm = {
                        toEmail: userClient == null ? "" : userClient.clientEmail,
                        ccEmail: this.setting.emailCc,
                        bccEmail: this.setting.emailBcc,
                        subject: this.attachmentName + " from " + this.companyName,
                        content: this.setting.emailMessage
                    };
                    this.batchMode = false;
                    this.sendDialogVisible = true;
                } catch (error) {
                    reportHandledError(error, {
                        module: 'document_actions',
                        operation: 'open_send_dialog',
                        context: {createType: this.createType}
                    });
                    this.$message.error(error.message);
                } finally {
                    loading.close();
                }
            },
            openBatchSendDialog(rows) {
                if (!Array.isArray(rows) || rows.length === 0) {
                    return;
                }
                if (rows.length === 1) {
                    this.openSendDialog(rows[0]);
                    return;
                }
                const documentPrefix = this.createType === 0 ? 'Invoice' : this.documentName;
                this.batchDocuments = rows.map(row => {
                    const pdfName = row.numPrefix + row.num + '.pdf';
                    const attachmentName = documentPrefix + '-' + pdfName;
                    return {
                        invoiceUUID: row.innerUUID,
                        toEmail: row.userClientEmail,
                        ccEmail: this.setting.emailCc,
                        bccEmail: this.setting.emailBcc,
                        subject: attachmentName + ' from ' + this.companyName,
                        content: this.setting.emailMessage,
                        pdfName: pdfName,
                        attachmentName: attachmentName,
                        status: '',
                        errorMessage: ''
                    };
                });
                this.expandedBatchDocuments = this.batchDocuments[0].invoiceUUID;
                this.batchMode = true;
                this.sendDialogVisible = true;
            },
            buildSendParams(document) {
                const params = {
                    invoiceUUID: document.invoiceUUID,
                    toEmail: document.toEmail,
                    subject: document.subject,
                    copyEmail: document.ccEmail,
                    secretEmail: document.bccEmail,
                    content: document.content,
                    pdfName: document.pdfName,
                    timeont: moment.tz.guess(),
                    nowTime: timest(),
                    createType: this.createType,
                    companyUUID: this.companyId
                };
                return params;
            },
            async sendEmail() {
                if (!validateEmail(this.sendForm.toEmail)) {
                    this.$message.error(this.$t('invoiceCreate.msgClientEmailError'));
                    return;
                }
                this.sendLoading = true;
                try {
                    const params = this.buildSendParams({
                        invoiceUUID: this.invoiceUUID,
                        toEmail: this.sendForm.toEmail,
                        subject: this.sendForm.subject,
                        ccEmail: this.sendForm.ccEmail,
                        bccEmail: this.sendForm.bccEmail,
                        content: this.sendForm.content,
                        pdfName: this.pdfName
                    });
                    const response = await this.$axios.post('send_email_to_client', {
                        params
                    });
                    if (response.data.errorCode === 0) {
                        this.sendDialogVisible = false;
                        this.$message.success(this.$t('invoiceCreate.mailSentSuccessfully'));
                        this.$emit('sent');
                    }
                } finally {
                    this.sendLoading = false;
                }
            },
            async sendBatchEmail() {
                const pendingDocuments = this.batchDocuments.filter(document => document.status !== 'sent');
                if (pendingDocuments.some(document => !validateEmail(document.toEmail))) {
                    this.$message.error(this.$t('common.batchEmailInvalidRecipients'));
                    return;
                }
                if (pendingDocuments.some(document => containsHtml(document.subject) || containsHtml(document.content))) {
                    this.$message.error(this.$t('common.emailHtmlNotAllowed'));
                    return;
                }
                this.sendLoading = true;
                let sentCount = this.batchDocuments.length - pendingDocuments.length;
                let failedCount = 0;
                try {
                    for (let i = 0; i < this.batchDocuments.length; i++) {
                        const document = this.batchDocuments[i];
                        if (document.status === 'sent') {
                            continue;
                        }
                        document.status = '';
                        document.errorMessage = '';
                        try {
                            const response = await this.$axios.post('send_email_to_client', {
                                params: this.buildSendParams(document)
                            });
                            if (response.data.errorCode === 0) {
                                document.status = 'sent';
                                sentCount++;
                            } else {
                                document.status = 'failed';
                                document.errorMessage = response.data.errorMsg;
                                failedCount++;
                            }
                        } catch (error) {
                            document.status = 'failed';
                            document.errorMessage = error.message;
                            failedCount++;
                        }
                    }
                    if (failedCount === 0) {
                        this.sendDialogVisible = false;
                        this.$message.success(this.$t('common.batchEmailSent', {count: sentCount}));
                        this.$emit('sent');
                    } else {
                        this.$message.error(this.$t('common.batchEmailPartial', {sent: sentCount, failed: failedCount}));
                    }
                } finally {
                    this.sendLoading = false;
                }
            }
        },
        computed: {
            selectedDocumentCount() {
                return this.batchDocuments.length;
            }
        }
    }
</script>

<style>
    .document-email-dialog {
        max-height: 90vh;
        margin-top: 5vh !important;
        display: flex;
        flex-direction: column;
        border-radius: 14px;
        overflow: hidden;
    }

    .document-email-dialog .el-dialog__header {
        padding: 24px 28px 18px;
        border-bottom: 1px solid #e8edf5;
    }

    .document-email-dialog .el-dialog__title {
        color: #17213c;
        font-size: 21px;
        font-weight: 700;
    }

    .document-email-dialog .el-dialog__body {
        padding: 20px 28px 8px;
        overflow-y: auto;
    }

    .document-email-dialog .el-dialog__footer {
        padding: 18px 28px 24px;
    }

    .document-email-form,
    .document-email-field {
        display: flex;
        flex-direction: column;
    }

    .document-email-form {
        gap: 14px;
    }

    .document-email-field {
        gap: 8px;
        min-width: 0;
    }

    .document-email-field label {
        color: #52627c;
        font-size: 13px;
        font-weight: 700;
    }

    .document-email-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
    }

    .document-email-dialog .el-input__inner,
    .document-email-dialog .el-textarea__inner {
        border-color: #dce4f0;
        border-radius: 8px;
        color: #263653;
        font-family: inherit;
    }

    .document-email-dialog .el-input__inner {
        height: 42px;
    }

    .document-email-attachment {
        min-height: 64px;
        padding: 12px 14px;
        display: flex;
        align-items: center;
        gap: 12px;
        box-sizing: border-box;
        background: #f7f9fd;
        border: 1px solid #e4eaf3;
        border-radius: 10px;
    }

    .document-email-pdf-icon {
        width: 38px;
        height: 42px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 38px;
        color: #ffffff;
        background: #ef4444;
        border-radius: 6px;
        font-size: 10px;
        font-weight: 800;
    }

    .document-email-attachment strong,
    .document-email-attachment span {
        display: block;
    }

    .document-email-attachment strong {
        color: #263653;
        font-size: var(--responsive-font-14);
        line-height: 20px;
    }

    .document-email-attachment span {
        margin-top: 2px;
        color: #8997ad;
        font-size: 12px;
    }

    .document-email-footer {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
    }

    .batch-email-intro {
        margin-bottom: 12px;
        color: #52627c;
        font-size: var(--responsive-font-14);
        font-weight: 700;
    }

    .batch-email-title {
        width: calc(100% - 24px);
        min-width: 0;
        display: grid;
        grid-template-columns: minmax(160px, 1fr) minmax(180px, 1fr) 22px;
        align-items: center;
        gap: 12px;
    }

    .batch-email-title strong,
    .batch-email-title span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .batch-email-title strong {
        color: #263653;
    }

    .batch-email-title span {
        color: #71809a;
        font-size: 13px;
    }

    .batch-email-document {
        padding: 8px 4px 20px;
    }

    .batch-email-success {
        color: #20a162;
    }

    .batch-email-failed,
    .batch-email-error {
        color: #e5484d;
    }

    .batch-email-error {
        font-size: 13px;
    }

    .document-action-loading-mask .el-loading-spinner {
        top: 50%;
        left: 50%;
        width: 220px;
        margin-top: 0;
        padding: 24px 28px;
        box-sizing: border-box;
        transform: translate(-50%, -50%);
        background: #ffffff;
        border: 1px solid #e1e8f2;
        border-radius: 12px;
        box-shadow: 0 16px 42px rgba(15, 23, 42, 0.2);
    }

    .document-action-loading-mask .el-loading-spinner i {
        color: #0d7df7;
        font-size: 30px;
    }

    .document-action-loading-mask .el-loading-text {
        margin-top: 12px;
        color: #263653;
        font-size: var(--responsive-font-14);
        font-weight: 600;
    }

    @media (max-width: 720px) {
        .document-email-dialog {
            width: calc(100% - 32px) !important;
        }

        .document-email-row {
            grid-template-columns: 1fr;
        }
    }
</style>
